import { useMemo, useState, useEffect } from "react";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useQuery } from "@tanstack/react-query";
import { CreditCard, Loader2, X, Plus, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/format";

const CARD_STYLE = {
  style: {
    base: {
      fontSize: "16px", color: "#1A242B", fontFamily: "inherit",
      "::placeholder": { color: "#8FA89B" },
    },
    invalid: { color: "#dc2626" },
  },
};

function CardChargeInner({ sale, client, onClose, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("new"); // "new" | payment_method_id
  const [saveCard, setSaveCard] = useState(true);

  const cardsQ = useQuery({
    queryKey: ["pos-cards", client?.id],
    queryFn: () => api.get(`/pos/clients/${client.id}/cards`).then((r) => r.data),
    enabled: !!client?.id,
  });
  const savedCards = cardsQ.data?.cards || [];

  useEffect(() => {
    if (savedCards.length > 0) setMode(savedCards[0].id);
  }, [savedCards.length]);

  const amount = Number(sale.balance_due || sale.total || 0);

  const pay = async () => {
    if (!stripe || !elements) return;
    setLoading(true);
    try {
      if (mode !== "new") {
        // Charge an existing saved card (server confirms off_session).
        const { data } = await api.post(`/pos/sales/${sale.id}/charge`, { payment_method_id: mode });
        if (data.status === "succeeded") return onPaid(data.sale);
        if (data.client_secret) {
          const res = await stripe.confirmCardPayment(data.client_secret);
          if (res.error) throw new Error(res.error.message);
          const c = await api.post(`/pos/sales/${sale.id}/charge/confirm`, { payment_intent_id: data.payment_intent_id });
          if (c.data.status === "succeeded") return onPaid(c.data.sale);
        }
        throw new Error("Card could not be charged.");
      }
      // New card entry.
      const { data } = await api.post(`/pos/sales/${sale.id}/charge`, { save_card: client ? saveCard : false });
      if (data.status === "succeeded") return onPaid(data.sale);
      const card = elements.getElement(CardElement);
      const res = await stripe.confirmCardPayment(data.client_secret, {
        payment_method: {
          card,
          billing_details: { name: client ? `${client.first_name} ${client.last_name}`.trim() : "POS sale" },
        },
      });
      if (res.error) throw new Error(res.error.message);
      const c = await api.post(`/pos/sales/${sale.id}/charge/confirm`, { payment_intent_id: data.payment_intent_id });
      if (c.data.status === "succeeded") return onPaid(c.data.sale);
      throw new Error("Payment did not complete.");
    } catch (e) {
      // A thrown Stripe.js error (card declined, wrong CVC, 3-D-Secure failed) has no
      // axios `response` — surface its real message instead of a generic fallback.
      const detail = e.response?.data?.detail;
      const msg = detail != null ? formatApiError(detail) : (e.message || "Payment failed. Please try another card.");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-7 max-h-[92vh] overflow-y-auto" data-testid="pos-card-modal">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-serif text-2xl text-[#1A242B] flex items-center gap-2"><CreditCard size={22} className="text-[#144F75]" /> Charge card</h3>
        <button onClick={onClose} data-testid="pos-card-cancel" className="text-[#4A5D6B] hover:text-[#144F75]"><X size={20} /></button>
      </div>
      <p className="text-sm text-[#4A5D6B] mb-4">Amount due <span className="font-semibold text-[#144F75]">{money(amount)}</span>{client ? ` · ${client.first_name} ${client.last_name}` : ""}</p>

      {savedCards.length > 0 && (
        <div className="space-y-2 mb-4">
          <p className="text-xs font-semibold text-[#4A5D6B] uppercase tracking-wide">Cards on file</p>
          {savedCards.map((c) => (
            <button
              key={c.id}
              data-testid={`pos-card-saved-${c.id}`}
              onClick={() => setMode(c.id)}
              className={`w-full flex items-center gap-3 rounded-xl border px-3.5 py-2.5 text-sm transition-colors ${mode === c.id ? "border-[#144F75] bg-[#144F75]/5" : "border-[#E5E0D8] hover:border-[#144F75]/50"}`}
            >
              <CreditCard size={16} className="text-[#144F75]" />
              <span className="capitalize">{c.brand} •••• {c.last4}</span>
              <span className="text-xs text-[#8FA89B] ml-auto">{String(c.exp_month).padStart(2, "0")}/{String(c.exp_year).slice(-2)}</span>
            </button>
          ))}
          <button
            data-testid="pos-card-use-new"
            onClick={() => setMode("new")}
            className={`w-full flex items-center gap-2 rounded-xl border px-3.5 py-2.5 text-sm transition-colors ${mode === "new" ? "border-[#144F75] bg-[#144F75]/5" : "border-[#E5E0D8] hover:border-[#144F75]/50"}`}
          >
            <Plus size={16} className="text-[#144F75]" /> Use a new card
          </button>
        </div>
      )}

      {mode === "new" && (
        <div className="space-y-3 mb-4">
          <div className="rounded-xl border border-[#E5E0D8] px-3.5 py-3.5 bg-white" data-testid="pos-card-element">
            <CardElement options={CARD_STYLE} />
          </div>
          {client && (
            <label className="flex items-center gap-2 text-sm text-[#2A3942] cursor-pointer">
              <input type="checkbox" data-testid="pos-card-save-toggle" checked={saveCard} onChange={(e) => setSaveCard(e.target.checked)} className="accent-[#144F75]" />
              Save this card on file for {client.first_name}
            </label>
          )}
        </div>
      )}

      <button
        onClick={pay}
        disabled={loading || !stripe}
        data-testid="pos-card-pay-btn"
        className="eyw-btn w-full justify-center disabled:opacity-60"
      >
        {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={16} />} Charge {money(amount)}
      </button>
      <p className="text-[11px] text-[#8FA89B] text-center mt-3 flex items-center justify-center gap-1">
        <Lock size={11} /> Card details are entered securely in Stripe — never stored on our servers.
      </p>
    </div>
  );
}

export default function CardChargeModal({ sale, client, publishableKey, onClose, onPaid }) {
  const stripePromise = useMemo(() => (publishableKey ? loadStripe(publishableKey) : null), [publishableKey]);
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-4 bg-black/40 backdrop-blur-sm">
      {stripePromise ? (
        <Elements stripe={stripePromise}>
          <CardChargeInner sale={sale} client={client} onClose={onClose} onPaid={onPaid} />
        </Elements>
      ) : (
        <div className="bg-white rounded-2xl p-6 max-w-sm text-center">
          <p className="text-sm text-[#4A5D6B]">Stripe is not configured. Add a publishable key to take cards here.</p>
          <button onClick={onClose} className="eyw-btn mt-4">Close</button>
        </div>
      )}
    </div>
  );
}
